import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type SharedAssetPlayerAsset = {
  uid: string
  name: string
  type: string
  isFolder?: boolean
  thumbnailUrl?: string | null
  /**
   * An asset the member reads in the organization's pool, when this one is a file linked to it on a
   * shelf they do not see: the player is opened through it, and the asset's own page is not offered.
   */
  viaAssetId?: string
}

/**
 * Where the shared player is currently displayed:
 * - `closed`: no player mounted
 * - `mini`: docked floating mini-player
 * - `modal`: preview modal
 */
export type SharedAssetPlayerMode = 'closed' | 'mini' | 'modal'

/** What the shared player lets the shell drive without reaching into its DOM. */
export type SharedAssetPlayerControls = {
  play: () => void
  pause: () => void
}

/**
 * Builds a SharedAssetPlayerAsset from any asset-like object, keeping only the fields
 * the mini-player needs. Avoids repeating the same field mapping at each call site.
 */
export function toSharedAssetPlayerAsset(source: SharedAssetPlayerAsset): SharedAssetPlayerAsset {
  return {
    uid: source.uid,
    name: source.name,
    type: source.type,
    isFolder: source.isFolder,
    thumbnailUrl: source.thumbnailUrl,
    viaAssetId: source.viaAssetId,
  }
}

/**
 * Owns the single, app-level AssetPlayer instance shared between the floating
 * mini-player and the preview modal.
 *
 * A single `<AssetPlayer>` lives in `SharedAssetPlayer` and is moved between
 * hosts with `<Teleport>`, so switching mini ↔ modal reparents the DOM without
 * unmounting the player: playback position, buffer and HLS session are kept.
 * `teleportTarget` resolves the active host from `mode`, falling back to a hidden
 * parking host so the target is never null while the player is mounted.
 */
export const useSharedAssetPlayerStore = defineStore('sharedAssetPlayer', () => {
  const asset = ref<SharedAssetPlayerAsset | null>(null)
  const position = ref(0)
  const mode = ref<SharedAssetPlayerMode>('closed')
  /** Whether the shared player is playing, from its own play / pause / ended events. */
  const playing = ref(false)
  let controls: SharedAssetPlayerControls | null = null

  const miniHost = ref<HTMLElement | null>(null)
  const modalHost = ref<HTMLElement | null>(null)
  const parkingHost = ref<HTMLElement | null>(null)

  const teleportTarget = computed<HTMLElement | null>(() => {
    if (mode.value === 'modal') return modalHost.value ?? parkingHost.value
    if (mode.value === 'mini') return miniHost.value ?? parkingHost.value
    return parkingHost.value
  })

  /** Resets the playback position only when the played asset actually changes. */
  function load(target: SharedAssetPlayerAsset, startAt: number) {
    if (asset.value?.uid !== target.uid) {
      position.value = startAt
      playing.value = false
    }
    asset.value = target
  }

  /** Opens (or keeps) the asset in the docked mini-player. */
  function openMini(target: SharedAssetPlayerAsset, startAt = 0) {
    load(target, startAt)
    mode.value = 'mini'
  }

  /** Opens (or keeps) the asset in the preview modal. */
  function openModal(target: SharedAssetPlayerAsset, startAt = 0) {
    load(target, startAt)
    mode.value = 'modal'
  }

  /** Switches the currently playing asset to the docked mini-player (minimize). */
  function toMini() {
    if (asset.value) mode.value = 'mini'
  }

  function close() {
    mode.value = 'closed'
    asset.value = null
    position.value = 0
    playing.value = false
  }

  function setPlaying(value: boolean) {
    playing.value = value
  }

  /** Registered by `SharedAssetPlayer`, which owns the player instance. */
  function setControls(value: SharedAssetPlayerControls | null) {
    controls = value
  }

  /** Plays or pauses the shared player: the phone's mini player bar has no room for its controls. */
  function togglePlayback() {
    if (playing.value) controls?.pause()
    else controls?.play()
  }

  /** Records the current playback time of the shared player. */
  function setPosition(time: number) {
    position.value = time
  }

  /** Whether the shared player is currently showing this asset. */
  function isPlaying(uid: string | null | undefined): boolean {
    return mode.value !== 'closed' && !!uid && asset.value?.uid === uid
  }

  function setMiniHost(el: HTMLElement | null) {
    miniHost.value = el
  }

  function setModalHost(el: HTMLElement | null) {
    modalHost.value = el
  }

  function setParkingHost(el: HTMLElement | null) {
    parkingHost.value = el
  }

  /** Releases a host element, no-op if it is no longer the registered one. */
  function clearHost(el: HTMLElement | null) {
    if (el && miniHost.value === el) miniHost.value = null
    if (el && modalHost.value === el) modalHost.value = null
  }

  return {
    asset,
    position,
    mode,
    playing,
    teleportTarget,
    openMini,
    openModal,
    toMini,
    close,
    setPosition,
    setPlaying,
    setControls,
    togglePlayback,
    isPlaying,
    setMiniHost,
    setModalHost,
    setParkingHost,
    clearHost,
  }
})
